import * as actionTypes from './workspacesActionTypes';
import piction from '../utils/piction';
import * as constants from '../constants/workspaces';
import { SESSION_EXPIRED_MESSAGE } from '../constants/sessionStateTypes';
import { sessionExpired } from './session';
import appConfig from '../appConfig';
import _ from 'lodash';

export const cancelAllRequests = () => ({
  type: actionTypes.WORKSPACES_CANCEL_ALL_REQUESTS,
});

export const cancelCreateRequest = (workspaceName, workspaceParentID = '0') => ({
  type: actionTypes.WORKSPACES_CANCEL_CREATE_REQUEST,
  workspaceName,
  workspaceParentID,
});

export const createWorkspaceRequest = (
  workspaceName, workspaceParentID, workspaceDescription, requestPromise
) => ({
  type: actionTypes.WORKSPACES_CREATE_REQUEST,
  workspaceName,
  workspaceParentID,
  workspaceDescription,
  requestPromise,
});

export const createWorkspaceSuccess = (
  workspaceID, workspaceName, workspaceParentID, workspaceDescription
) => ({
  type: actionTypes.WORKSPACES_CREATE_SUCCESS,
  workspaceID,
  workspaceName,
  workspaceParentID,
  workspaceDescription,
});

export const createWorkspaceFailure = (workspaceName, workspaceParentID, err) => ({
  type: actionTypes.WORKSPACES_CREATE_FAILURE,
  workspaceName,
  workspaceParentID,
  err,
});

export const createWorkspace = (workspaceName, workspaceParentID = '0', workspaceDescription = '') =>
  function createWorkspacesDispatch(dispatch, getState) {
    const store = getState().workspaces;

    const foundRequestIndex = store.get('createRequests').findKey(createRequest =>
      createRequest.get('workspaceName') === workspaceName &&
      createRequest.get('workspaceParentID') === workspaceParentID
    );

    if (foundRequestIndex !== undefined &&
      !(store.getIn(['createRequests', foundRequestIndex, 'request']) instanceof Error)
    ) {
      return Promise.resolve(new Error('Request with same details already being made'));
    }

    const promise = piction.createLightbox(workspaceName,
      { PARENTID: workspaceParentID, DESCRIPTION: workspaceDescription }
    )
      .then((res) => {
        dispatch(
          createWorkspaceSuccess(res.id, workspaceName, workspaceParentID, workspaceDescription)
        );
        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          dispatch(sessionExpired(err));
        } else {
          dispatch(createWorkspaceFailure(workspaceName, workspaceParentID, err));
        }
        return err;
      });

    dispatch(
      createWorkspaceRequest(workspaceName, workspaceParentID, workspaceDescription, promise)
    );
    return promise;
  };

export const deleteWorkspaceRequest = (workspaceID, requestPromise) => ({
  type: actionTypes.WORKSPACES_DELETE_REQUEST,
  workspaceID,
  requestPromise,
});

export const deleteWorkspaceSuccess = workspaceID => ({
  type: actionTypes.WORKSPACES_DELETE_SUCCESS,
  workspaceID,
});

export const deleteWorkspaceFailure = (workspaceID, err) => ({
  type: actionTypes.WORKSPACES_DELETE_FAILURE,
  workspaceID,
  err,
});

export const deleteWorkspace = workspaceID => function deleteWorkspaceDispatch(dispatch) {
  const promise = piction.deleteLightbox(workspaceID)
    .then((res) => {
      dispatch(deleteWorkspaceSuccess(workspaceID, res.res.ct));
      return res;
    })
    .catch((err) => {
      if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
        dispatch(sessionExpired(err));
      } else {
        dispatch(deleteWorkspaceFailure(workspaceID, err));
      }
      return err;
    });

  dispatch(deleteWorkspaceRequest(workspaceID, promise));
  return promise;
};

export const editWorkspaceRequest = (workspaceID, workspaceName, workspaceDescription, requestPromise) => ({
  type: actionTypes.WORKSPACES_EDIT_REQUEST,
  workspaceID,
  workspaceName,
  workspaceDescription,
  requestPromise,
});

export const editWorkspaceSuccess = (workspaceID, workspaceName, workspaceDescription) => ({
  type: actionTypes.WORKSPACES_EDIT_SUCCESS,
  workspaceID,
  workspaceName,
  workspaceDescription,
});

export const editWorkspaceFailure = (workspaceID, err) => ({
  type: actionTypes.WORKSPACES_EDIT_FAILURE,
  workspaceID,
  err,
});

export const editWorkspace = (
  workspaceID,
  workspaceName,
  workspaceDescription,
  workspaceUsers,
  workspaceRemoveUsers,
  expiryDate,
  securityLevel,
  downloadRule
) =>
  function retrieveWorkspacesDispatch(dispatch) {
    const options = {
      NAME: workspaceName,
      DESCRIPTION: workspaceDescription,
      MAP_ROLE: 'TRUE',
      MAP_PROFILE: workspaceUsers,
      REMOVE_PROFILE: workspaceRemoveUsers,
      ACCESS: (securityLevel || appConfig.secureWorkspaceShareRoles) ? 'SECURE' : 'PUBLIC',
      NEW_USER_TEMPLATE: 'KAUI2.Workspace_Share_Email',
      WORKSPACE_EXPIRE_DATE_FROM: expiryDate,
      EDIT_RULE: 'TRUE',
      EXTERNAL_UD1: downloadRule,
    };
    const promise = piction.editLightbox(workspaceID, options)
      .then((res) => {
        dispatch(editWorkspaceSuccess(workspaceID, workspaceName, workspaceDescription));
        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          dispatch(sessionExpired(err));
        } else {
          dispatch(editWorkspaceFailure(workspaceID, err));
        }
        return err;
      });

    dispatch(editWorkspaceRequest(workspaceID, workspaceName, workspaceDescription, promise));
    return promise;
  };

export const retrieveWorkspacesRequest = (workspaceID, requestPromise) => ({
  type: actionTypes.WORKSPACES_RETRIEVE_REQUEST,
  workspaceID,
  requestPromise,
});

export const retrieveWorkspacesSuccess = (workspaceID, workspaceList) => ({
  type: actionTypes.WORKSPACES_RETRIEVE_SUCCESS,
  workspaceID,
  workspaceList,
});

export const retrieveWorkspacesFailure = (workspaceID, err) => ({
  type: actionTypes.WORKSPACES_RETRIEVE_FAILURE,
  workspaceID,
  err,
});

export const retrieveWorkspaces = workspaceID => function retrieveWorkspacesDispatch(dispatch) {
  const promise = piction.getLightboxes(null, { CATEGORY_ID: workspaceID })
    .then((res) => {
      dispatch(retrieveWorkspacesSuccess(workspaceID, res.res.ct));
      return res;
    })
    .catch((err) => {
      if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
        dispatch(sessionExpired(err));
      } else {
        dispatch(retrieveWorkspacesFailure(workspaceID, err));
      }
      return err;
    });

  dispatch(retrieveWorkspacesRequest(workspaceID, promise));
  return promise;
};

export const clearPanelTreeList = categoryID => ({
  type: actionTypes.WORKSPACE_LIST_CLEAR,
  categoryID,
});

export const retrieveWorkspaceInfoRequest = (workspaceID, requestPromise) => ({
  type: actionTypes.WORKSPACES_RETRIEVE_INFO_REQUEST,
  workspaceID,
  requestPromise,
});

export const retrieveWorkspaceInfoSuccess =
  (workspaceID, workspaceUsers, isOwner, shareExpDate) => ({
    type: actionTypes.WORKSPACES_RETRIEVE_INFO_SUCCESS,
    workspaceID,
    workspaceUsers,
    isOwner,
    shareExpDate,
  });

export const retrieveWorkspaceInfoFailure = (workspaceID, err) => ({
  type: actionTypes.WORKSPACES_RETRIEVE_INFO_FAILURE,
  workspaceID,
  err,
});

export const retrieveWorkspaceInfo = workspaceID =>
  function retrieveWorkspacesInfoDispatch(dispatch) {
    const promise = piction.request('Lightbox_query', {
      CATEGORY_ID: workspaceID,
      VIEW_ROLE: 'TRUE',
      SHOW_DOWNLOAD_RULES: 'TRUE',
    })
      .then((res) => {
        if (res && res.a) {
          let users = res.a.user || [];
          users = _.isArray(users) ? users : users.toArray();
          let security = appConfig.secureWorkspaceShareRoles;
          if (res.a.SEC && res.a.SEC[0]) {
            security = res.a.SEC[0]._ !== 'N'; // N = Public, S/X = Secure
          }

          const resData = {
            users,
            isOwner: res.a.IS_OWNER && res.a.IS_OWNER[0] === 'T',
            ownerName: (res.a.OWNER && res.a.OWNER[0]._) || '',
            expiryDate: (res.a.EXPIRE_DATE_FROM && res.a.EXPIRE_DATE_FROM[0]) || '',
            downloadRule: (res.a.EXTERNAL_UD1 && res.a.EXTERNAL_UD1[0]) || '',
            downRuleName: (res.a.EXTERNAL_UD1_NAME && res.a.EXTERNAL_UD1_NAME[0]) || '',
            security,
          };
          dispatch(retrieveWorkspaceInfoSuccess(
            workspaceID, users, resData.isOwner, resData.expiryDate));
          return resData;
        } else if ((res.message && res.message.includes(SESSION_EXPIRED_MESSAGE)) ||
          (res.RETURN_MESSAGE && res.RETURN_MESSAGE.includes(SESSION_EXPIRED_MESSAGE))) {
          dispatch(sessionExpired(res));
        }
        return res;
      })
      .catch((err) => {
        if ((err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) ||
          (err.RETURN_MESSAGE && err.RETURN_MESSAGE.includes(SESSION_EXPIRED_MESSAGE))) {
          dispatch(sessionExpired(err));
        } else {
          dispatch(retrieveWorkspaceInfoFailure(workspaceID, err));
        }
        return err;
      });

    dispatch(retrieveWorkspaceInfoRequest(workspaceID, promise));
    return promise;
  };

export const workspaceUnsubscribeRequest = (workspaceID, requestPromise) => ({
  type: actionTypes.WORKSPACES_UNSUBSCRIBE_REQUEST,
  workspaceID,
  requestPromise,
});

export const workspaceUnsubscribeSuccess = (workspaceID, workspaceList) => ({
  type: actionTypes.WORKSPACES_UNSUBSCRIBE_SUCCESS,
  workspaceID,
  workspaceList,
});

export const workspaceUnsubscribeFailure = (workspaceID, err) => ({
  type: actionTypes.WORKSPACES_UNSUBSCRIBE_FAILURE,
  workspaceID,
  err,
});

export const workspaceUnsubscribe = workspaceID => function retrieveWorkspacesDispatch(dispatch) {
  const promise = piction.request('UNSUBSCRIBE_WORKSPACE', {
    CATEGORY_ID: workspaceID,
    TEMPLATE: 'KAUI2.Portal_Unsubscribe_Email',
  })
    .then((res) => {
      if (res.a && res.a.RETURN_MESSAGE && res.a.RETURN_MESSAGE[0] === 'TRUE') {
        dispatch(workspaceUnsubscribeSuccess(workspaceID, res));
      } else {
        dispatch(workspaceUnsubscribeFailure(workspaceID, res));
      }
      return res;
    })
    .catch((err) => {
      console.log(err);
      if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
        dispatch(sessionExpired(err));
      } else {
        dispatch(workspaceUnsubscribeFailure(workspaceID, err));
      }
      return err;
    });

  dispatch(workspaceUnsubscribeRequest(workspaceID, promise));
  return promise;
};
